/**
 * Sentry error tracking integration for the frontend.
 *
 * Gracefully degrades when `VITE_SENTRY_DSN` is not set:
 * all exports become safe no-ops so the rest of the app is unaffected.
 *
 * To activate: set the `VITE_SENTRY_DSN` environment variable.
 */

import * as Sentry from "@sentry/react";

let initialized = false;

/**
 * Initialize Sentry error tracking.
 *
 * Must be called before `ReactDOM.createRoot`. Does nothing if
 * `VITE_SENTRY_DSN` is not set.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.debug("[Sentry] No VITE_SENTRY_DSN set — skipping initialization");
    }
    return;
  }

  const isProd = import.meta.env.MODE === "production";

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: isProd ? 0.2 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });

  initialized = true;

  if (import.meta.env.DEV) {
    console.debug("[Sentry] Initialized successfully");
  }
}

/**
 * Report an exception to Sentry.
 * No-ops if Sentry is not initialized.
 */
export function captureException(
  error: unknown,
  context?: { extra?: Record<string, unknown> },
): void {
  if (!initialized) return;

  Sentry.captureException(error, {
    extra: context?.extra,
  });
}

/**
 * Send a message to Sentry.
 * No-ops if Sentry is not initialized.
 */
export function captureMessage(
  message: string,
  level?: "fatal" | "error" | "warning" | "info" | "debug",
): void {
  if (!initialized) return;

  Sentry.captureMessage(message, level);
}

/**
 * Set or clear the current user context in Sentry.
 * Call with `null` on logout to clear user data.
 */
export function setUser(
  user: { id: string; username: string; role: string } | null,
): void {
  if (!initialized) return;

  if (user) {
    Sentry.setUser({
      id: user.id,
      username: user.username,
      data: { role: user.role },
    });
  } else {
    Sentry.setUser(null);
  }
}
